// SkuFamilyBuilderDialog — SIMPLIFIED step-by-step wizard (iter_116 rework).
//
// UX (per user request):
//   Step 1: 基本情報 — 分類名 + コード + 一言説明
//   Step 2: 大分類 (required) — pick a type (固定文字/固定数字/年4/年2/月/連番) + configure
//   Step 3: 中分類 (optional) — same picker or スキップ
//   Step 4: 小分類 (optional) — only if 中分類 added
//   Step 5: プレビュー — concatenated sample SKU + save
//
// The wizard produces the exact same `segments[]` shape the backend expects
// (see `POST /api/sku/templates`), plus a `bucket_keys=[]` and
// `length = sum(segment.width)`.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Hash, ArrowRight, ArrowLeft, Check, Sparkle, Trash, PencilSimple, Warning, Plus,
} from '@phosphor-icons/react';
import SegmentPicker from './SegmentPicker';
import {
  SEG_TYPE_BY_KEY,
  SEPARATOR_LABEL,
  isSeparatorSeg,
  buildSegment,
  reverseSegment,
  previewSegment,
  slugify,
  randomCode,
  CODE_RE,
} from './sku-segment-helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── 対象アプリ (app_code scope) ─────────────────────────────────────────
// Extensible: add new apps here to make them selectable in the rule editor.
// The SKU engine doesn't care about the string — it's just a filter tag used
// by SkuWizard / picker components to narrow the visible template list.
// Corresponding visual tones live in components/SkuWizard.js::APP_TONES.
const APP_CHOICES = [
  { code: '',                 label: 'すべてのアプリで使用可', hint: 'アプリを絞り込まず、どこからでも呼び出せます' },
  { code: 'seizo',            label: '製造 (Seizo)',           hint: 'mfg/inventory の品目登録から呼び出せます' },
  { code: 'shizai',           label: '資材 (Shizai)',          hint: '部品受注入力・在庫依頼で使用します' },
  { code: 'it_assets',        label: 'IT資産管理',             hint: '将来のIT資産管理アプリで使用（拡張予定）' },
  { code: 'office_equipment', label: 'オフィス備品管理',       hint: '将来のオフィス備品管理アプリで使用（拡張予定）' },
  { code: 'engineering',      label: '技術資産 (Engineering)', hint: '技術資産の登録画面から呼び出せます' },
  { code: 'other',            label: 'その他',                 hint: 'カスタムアプリ・仮スコープ' },
];

// ═══════════════════════════════════════════════════════════════════════
// Main dialog
// ═══════════════════════════════════════════════════════════════════════
export default function SkuFamilyBuilderDialog({ open, onOpenChange, onSaved, initial }) {
  const editing = !!initial?.id;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [basic, setBasic] = useState({ code: '', name: '', description: '' });
  const [assetKind, setAssetKind] = useState('');
  const [cat1, setCat1] = useState(null);
  const [cat2, setCat2] = useState(null);
  const [cat3, setCat3] = useState(null);
  const [use2, setUse2] = useState(false);
  const [use3, setUse3] = useState(false);
  const [legacyWarning, setLegacyWarning] = useState(false);
  // iter_138 · 対象アプリ scope (app_code). Empty string = "すべてのアプリ".
  // Moved into 詳細設定 per user 20260721 · "対象アプリ does not matter".
  const [appCode, setAppCode] = useState('');
  // iter_138: hide the code field behind a "詳細設定" expander. Users answered
  // ask_human option (b) — auto-derive from name if English, auto-generate
  // random `tpl_xxxxxx` if Japanese-only, expose only for admins who care about
  // the URL-friendly slug (audit logs, deep-link filtering, etc.).
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  // 2026-11-03 · 品番グループ（必須）。番号だけ出てグループが無いルールは作らせない。
  const [skuGroups, setSkuGroups] = useState([]);
  const [assetCats, setAssetCats] = useState([]);
  const [groupCode, setGroupCode] = useState('');
  const [newGroup, setNewGroup] = useState(null);   // {code, name_ja, default_category_id}

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setLegacyWarning(false);
    if (initial) {
      setBasic({ code: initial.code || '', name: initial.name || '', description: initial.description || '' });
      setAssetKind(initial.asset_kind || '');
      setAppCode(initial.app_code || '');
      const segs = initial.segments || [];
      // iter_144a · Collapse "category, separator, category" triples back into
      // the wizard's per-category `nextSep` sub-field so editing an existing
      // rule that was built with trailing separators shows cleanly.
      // iter_144b · Prefer the deterministic SEPARATOR_LABEL marker over the
      // 1-char-non-alphanumeric heuristic when detecting the middle segment.
      const collapsed = [];
      for (let i = 0; i < segs.length; i += 1) {
        const s = segs[i];
        const next = segs[i + 1];
        const isMarkerSep = next && isSeparatorSeg(next);
        const isLegacySep = next
          && next.kind === 'literal'
          && next.width === 1
          && !/[a-zA-Z0-9]/.test(String(next.value || ''))
          && next.label !== SEPARATOR_LABEL;   // avoid double-detecting a marker
        const isSep1char = isMarkerSep || isLegacySep;
        const revd = reverseSegment(s);
        if (!revd) { collapsed.push(null); continue; }
        if (isSep1char && revd.type !== 'separator' && segs[i + 2]) {
          revd.nextSep = String(next.value || '').slice(0, 1);
          collapsed.push(revd);
          i += 1; // skip the separator we just absorbed
        } else {
          collapsed.push(revd);
        }
      }
      if (collapsed.some((s) => s === null)) {
        setLegacyWarning(true);
        setCat1(null); setCat2(null); setCat3(null); setUse2(false); setUse3(false);
      } else {
        setCat1(collapsed[0] || null);
        setCat2(collapsed[1] || null); setUse2(!!collapsed[1]);
        setCat3(collapsed[2] || null); setUse3(!!collapsed[2]);
      }
    } else {
      setBasic({ code: '', name: '', description: '' });
      setAssetKind('');
      setAppCode('');
      setCat1(null); setCat2(null); setCat3(null);
      setUse2(false); setUse3(false);
    }
    setShowAdvanced(false);
    setCodeManuallyEdited(false);
    setGroupCode(initial?.sku_group_code || '');
    setNewGroup(null);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/engineering/sku-groups`, { withCredentials: true })
      .then((r) => setSkuGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSkuGroups([]));
    axios.get(`${API}/engineering/asset-categories`, { withCredentials: true })
      .then((r) => setAssetCats(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAssetCats([]));
  }, [open]);

  // iter_143k · Fetch existing templates once the dialog opens so we can
  // warn about duplicates in real time. Per user request "make sure no
  // other same rule and 分類" — surface conflicts before the backend 409.
  const [existingTemplates, setExistingTemplates] = useState([]);
  useEffect(() => {
    if (!open) { setExistingTemplates([]); return; }
    axios.get(`${API}/sku/templates`, { withCredentials: true })
      .then((r) => setExistingTemplates(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExistingTemplates([]));
  }, [open]);

  // iter_142 · 部門 field removed per user request 20260721. Templates remain
  // PUBLIC (shared across departments); the picker no longer shows a dept chip.

  // Auto-derive code from name (English) or generate random tpl_xxxxxx (Japanese-only).
  // Skips if the user has manually edited the code via the 詳細設定 expander.
  // NOTE: `randomCode()` returns a NEW value every call, so we must only invoke
  // it when the current code is empty or was manually cleared — otherwise the
  // effect re-runs on every setBasic and loops. Once a stable `tpl_xxx` is set
  // for a Japanese-only name, we keep it until the name changes to something
  // sluggable or the user clicks 再生成.
  // iter_144d · When the auto-derived code collides with an existing rule's
  // code (which now happens routinely because the user can save multiple
  // rules under the SAME NAME after iter_144d removed the dupName block),
  // append `_2`, `_3`, … until we find a free slot. Skips the rule being
  // edited so opening an existing rule doesn't cause it to churn.
  const pickAvailableCode = (base, existing, excludeId) => {
    const used = new Set(
      existing
        .filter((t) => t.id !== excludeId)
        .map((t) => (t.code || '').toLowerCase())
    );
    if (!used.has(base.toLowerCase())) return base;
    for (let i = 2; i < 1000; i += 1) {
      const candidate = `${base}_${i}`;
      if (!used.has(candidate.toLowerCase())) return candidate;
    }
    return base;   // give up after 999 attempts — dupCode will still block
  };

  useEffect(() => {
    if (editing || codeManuallyEdited) return;
    if (!basic.name) {
      if (basic.code) setBasic((b) => ({ ...b, code: '' }));
      return;
    }
    const derived = slugify(basic.name);
    if (derived.length >= 2) {
      // Name is sluggable — sync code to it, but auto-suffix on collision so
      // the user can literally reuse the same NAME without diving into 詳細設定.
      const next = pickAvailableCode(derived, existingTemplates, initial?.id);
      if (next !== basic.code) {
        setBasic((b) => ({ ...b, code: next }));
      }
    } else {
      // Name is Japanese-only — assign a random code ONCE and keep it stable.
      // Only generate a fresh random code if we currently have nothing or an
      // old slugified value from a previous English name.
      if (!basic.code || !basic.code.startsWith('tpl_')) {
        setBasic((b) => ({ ...b, code: randomCode() }));
      }
    }
  }, [basic.name, basic.code, editing, codeManuallyEdited, existingTemplates, initial?.id]);

  // Live preview: build segments the way the backend will see them.
  // iter_144a · When a category has `nextSep`, emit an EXTRA separator
  // segment right after it, so "ITM" + nextSep="-" → 2 segments (ITM, -).
  // Only inject the separator when there is a NEXT category to separate
  // from — trailing separators are meaningless.
  const segments = useMemo(() => {
    const out = [];
    const injectSep = (seg, key) => {
      if (!seg?.nextSep) return;
      const sepWiz = { type: 'separator', value: seg.nextSep };
      out.push({ ...buildSegment(sepWiz, key), _wiz: sepWiz, _autoSep: true });
    };
    const nextExists = (idx) => {
      if (idx === 0) return use2 && !!cat2?.type;
      if (idx === 1) return use3 && !!cat3?.type;
      return false;
    };
    if (cat1?.type) {
      out.push({ ...buildSegment(cat1, 'cat1'), _wiz: cat1 });
      if (nextExists(0)) injectSep(cat1, 'sep1');
    }
    if (use2 && cat2?.type) {
      out.push({ ...buildSegment(cat2, 'cat2'), _wiz: cat2 });
      if (nextExists(1)) injectSep(cat2, 'sep2');
    }
    if (use3 && cat3?.type) out.push({ ...buildSegment(cat3, 'cat3'), _wiz: cat3 });
    return out;
  }, [cat1, cat2, cat3, use2, use3]);

  const totalWidth = segments.reduce((s, x) => s + (x.width || 0), 0);
  const previewSku = segments.map((s) => previewSegment(s._wiz)).join('');

  const hasDynamic = segments.some((s) => ['counter', 'year4', 'year2', 'month'].includes(s.kind));

  // このルールが出す接頭辞 — 新しいグループのコードはこれに揃える（番号と必ず一致させる）。
  const rulePrefix = ((previewSku.match(/^([A-Za-z]{2,5})/) || [])[1] || '').toUpperCase();
  useEffect(() => {
    if (!open || groupCode || newGroup || !rulePrefix || !skuGroups.length) return;
    if (skuGroups.some((g) => g.code === rulePrefix)) setGroupCode(rulePrefix);
  }, [open, groupCode, newGroup, rulePrefix, skuGroups]);

  // iter_143k · Duplicate matches — computed AFTER `segments` is defined
  // (TDZ trap: moving these above segments broke the whole dialog).
  // iter_144d · `dupName` check was REMOVED per user "the rule name IS the
  // category, so same name should be same". Multiple rules can now share a
  // display name (they are still uniquely identified by their code). Same-name
  // rules are grouped together in `SkuTemplatesCard`.
  const dupCode = useMemo(() => {
    const c = (basic.code || '').trim().toLowerCase();
    if (!c) return null;
    return existingTemplates.find((t) => (t.code || '').toLowerCase() === c && t.id !== initial?.id) || null;
  }, [basic.code, existingTemplates, initial?.id]);
  const segmentsFingerprint = useMemo(() => {
    return segments.map((s) => `${s.kind}:${s.value || ''}:${s.width || ''}`).join('|');
  }, [segments]);
  const dupShape = useMemo(() => {
    if (!segmentsFingerprint) return null;
    return existingTemplates.find((t) => {
      if (t.id === initial?.id) return false;
      const fp = (t.segments || []).map((s) => `${s.kind}:${s.value || ''}:${s.width || ''}`).join('|');
      return fp && fp === segmentsFingerprint;
    }) || null;
  }, [segmentsFingerprint, existingTemplates, initial?.id]);

  // Validation
  const errors = [];
  if (!basic.name) errors.push('ルール名を入力してください');
  if (!basic.code) errors.push('コードを入力してください');
  // iter_143k · Duplicate guards — block save if code/name/shape collides
  // with an existing template (excluding the one being edited).
  if (dupCode) errors.push(`コード「${basic.code}」は既に使われています（ルール名: ${dupCode.name}）`);
  if (dupShape) errors.push(`同じ分類パターンのルール「${dupShape.name}」が既にあります`);
  if (!cat1?.type) errors.push('大分類の種別を選んでください');
  if (cat1?.type && !(cat1.label || '').trim()) errors.push('大分類の名前を入力してください');
  if (cat1?.type === 'letters' && !cat1.value) errors.push('大分類の文字を入力してください');
  if (cat1?.type === 'digits' && !cat1.value) errors.push('大分類の数字を入力してください');
  if (use2 && !cat2?.type) errors.push('中分類の種別を選んでください');
  if (use2 && cat2?.type && !(cat2.label || '').trim()) errors.push('中分類の名前を入力してください');
  if (use2 && cat2?.type === 'letters' && !cat2.value) errors.push('中分類の文字を入力してください');
  if (use2 && cat2?.type === 'digits' && !cat2.value) errors.push('中分類の数字を入力してください');
  if (use3 && !cat3?.type) errors.push('小分類の種別を選んでください');
  if (use3 && cat3?.type && !(cat3.label || '').trim()) errors.push('小分類の名前を入力してください');
  if (use3 && cat3?.type === 'letters' && !cat3.value) errors.push('小分類の文字を入力してください');
  if (use3 && cat3?.type === 'digits' && !cat3.value) errors.push('小分類の数字を入力してください');
  if (segments.length < 2) errors.push('大分類とあと1つ以上（中分類 または 連番など）が必要です');
  if (newGroup) {
    if (!/^[A-Z]{2,5}$/.test((newGroup.code || '').toUpperCase())) errors.push('新しい品番グループのコードは英字2〜5文字です');
    if (!(newGroup.name_ja || '').trim()) errors.push('新しい品番グループの名称を入力してください');
  } else if (!groupCode) {
    errors.push('品番グループを選んでください（無ければ「新しいグループを作成」）');
  }
  const canSave = errors.length === 0;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      // グループを先に作る — ルールだけ保存されてグループが無い状態を作らない。
      let groupForRule = groupCode;
      if (newGroup) {
        const code = (newGroup.code || '').toUpperCase();
        await axios.post(`${API}/engineering/sku-groups`, {
          code, name_ja: (newGroup.name_ja || '').trim(),
          default_category_id: newGroup.default_category_id || 'component',
          sort_order: 100,
        }, { withCredentials: true });
        groupForRule = code;
      }
      const cleanSegments = segments.map(({ _wiz: _drop, ...s }) => s);
      // iter_135 Phase 3: SkuFamilyBuilderDialog is repurposed as the
      // 品番ルール (template) editor. Saves POST to /api/sku/templates
      // (the Family API was deleted in this iteration).
      const payload = {
        code: basic.code,
        name: basic.name,
        description: basic.description,
        length: totalWidth,
        sample: previewSku,
        segments: cleanSegments,
        category: initial?.category || '',
        values: initial?.values || {},
        locked_keys: initial?.locked_keys || [],
        bucket_keys: initial?.bucket_keys || [],
        department_ids: initial?.department_ids || [],
        is_builtin: false,
        sort_order: initial?.sort_order || 100,
        active: initial?.active !== false,
        asset_kind: assetKind,
        sku_group_code: groupForRule,
        app_code: appCode,
        department: '',
        type_code: initial?.type_code || '',
      };
      let created = null;
      if (editing) {
        const r = await axios.put(`${API}/sku/templates/${initial.id}`, payload, { withCredentials: true });
        created = r.data;
        toast.success(`品番ルール「${basic.name}」を更新しました`);
      } else {
        const r = await axios.post(`${API}/sku/templates`, payload, { withCredentials: true });
        created = r.data;
        toast.success(`品番ルール「${basic.name}」を作成しました`);
      }
      onSaved?.(created);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const STEPS = ['基本情報', '大分類', '中分類', '小分類', 'プレビュー'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle weight="duotone" size={22} className="text-blue-600" />
            {editing ? '品番ルールを編集' : '品番ルールを新規作成'}
          </DialogTitle>
          <DialogDescription>
            順番に選ぶだけで、あなた専用の品番ルールが作れます。
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 text-[11px]" data-testid="sku-family-stepper">
          {STEPS.map((s, i) => {
            const skipped = (i === 2 && !use2) || (i === 3 && !use3);
            const done = i < step && !skipped;
            const active = i === step;
            return (
              <div key={s} className="flex items-center gap-1 flex-1">
                <button
                  type="button"
                  onClick={() => setStep(i)}
                  className={`flex-1 h-8 rounded-full px-2 font-semibold border-2 transition-all ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : done
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : skipped
                      ? 'bg-zinc-50 text-zinc-400 border-zinc-200 line-through'
                      : 'bg-white text-zinc-600 border-zinc-200'
                  }`}
                >
                  {done ? <Check size={12} className="inline mr-1" /> : `${i + 1}.`} {s}
                </button>
                {i < STEPS.length - 1 && <ArrowRight size={12} className="text-zinc-300 shrink-0" />}
              </div>
            );
          })}
        </div>

        {legacyWarning && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900">
            <Warning size={18} className="shrink-0 mt-0.5" />
            <div>
              この分類は旧式エディタで作成されており、シンプルウィザードでは編集できない項目を含んでいます。
              保存すると設定が置き換わります。よろしければ続けて、そうでなければキャンセルしてください。
            </div>
          </div>
        )}

        <div className="min-h-[280px]">
          {/* Step 0 — basic */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs flex items-center gap-1">
                  ルール名（グループ） <span className="text-rose-500">*</span>
                  <span className="text-[10px] text-zinc-400 font-normal">
                    · 同じグループ名にすると設定一覧でひとまとめに表示されます
                  </span>
                </Label>
                {/* iter_146 · Group picker per user "品番ルール is like group,
                    when I make a rule in wizard should what group then the
                    分類 will linked to that". Existing group names appear
                    as clickable chips so the user can attach this rule to
                    a sibling family with one click; the '+ 新規' chip
                    switches to a free-text input for a brand-new group. */}
                {(() => {
                  const groups = Array.from(
                    new Set(
                      existingTemplates
                        .map((t) => (t.name || '').trim())
                        .filter((n) => n && n !== initial?.name)
                    )
                  ).sort((a, b) => a.localeCompare(b, 'ja'));
                  const currentIsExisting = groups.includes((basic.name || '').trim());
                  return (
                    <div className="space-y-2 mt-1" data-testid="family-group-picker">
                      {groups.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {groups.map((g) => {
                            const active = basic.name === g;
                            return (
                              <button
                                key={g}
                                type="button"
                                onClick={() => setBasic({ ...basic, name: g })}
                                data-testid={`family-group-chip-${g}`}
                                className={`h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${
                                  active
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-400 hover:text-blue-700'
                                }`}
                              >
                                {g}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => setBasic({ ...basic, name: '' })}
                            data-testid="family-group-chip-new"
                            className={`h-8 px-3 rounded-full border-2 border-dashed text-xs font-semibold transition-colors ${
                              !currentIsExisting && basic.name !== ''
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                                : 'bg-white text-zinc-500 border-zinc-300 hover:border-emerald-400 hover:text-emerald-700'
                            }`}
                          >
                            + 新しいグループ
                          </button>
                        </div>
                      )}
                      <Input
                        data-testid="family-name-input"
                        value={basic.name}
                        onChange={(e) => setBasic({ ...basic, name: e.target.value })}
                        placeholder={
                          groups.length > 0
                            ? '↑ から選ぶか、新しいグループ名を入力…'
                            : '例: ソルダリング / センスビー / ヒーター / オフィス備品 / IT機器'
                        }
                      />
                      {basic.name && !currentIsExisting && groups.length > 0 && (
                        <div className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="family-group-new-hint">
                          <Sparkle size={11} weight="fill" />
                          新しいグループ「{basic.name}」を作成します
                        </div>
                      )}
                      {basic.name && currentIsExisting && (
                        <div className="text-[11px] text-blue-700 flex items-center gap-1" data-testid="family-group-existing-hint">
                          <Check size={11} weight="fill" />
                          既存グループ「{basic.name}」に追加します（一覧では同じ見出しの下にまとめて表示されます）
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!editing && basic.name && basic.code && (
                  <div className="text-[10px] text-zinc-400 mt-1" data-testid="family-code-hint">
                    保存時のコード: <span className="font-mono text-zinc-500">{basic.code}</span>
                    <button
                      type="button"
                      onClick={() => setShowAdvanced((v) => !v)}
                      className="ml-2 text-blue-600 hover:text-blue-700 underline"
                      data-testid="family-code-advanced-toggle"
                    >
                      {showAdvanced ? '閉じる' : '変更する'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">一言メモ（任意）</Label>
                <Input
                  data-testid="family-desc-input"
                  value={basic.description}
                  onChange={(e) => setBasic({ ...basic, description: e.target.value })}
                  placeholder="例: ソルダリング製造部で使う部品"
                />
              </div>
              {/* Advanced — code slug editor (opt-in). Hidden by default because
                  the auto-generated code is usually fine; only admins who want
                  URL-friendly slugs for audit-log filtering / deep links open this. */}
              {(editing || showAdvanced) && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 space-y-2" data-testid="family-code-advanced">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-zinc-700">
                      詳細設定 · コード（内部識別子）
                    </Label>
                    {!editing && (
                      <button
                        type="button"
                        onClick={() => {
                          setCodeManuallyEdited(false);
                          const derived = slugify(basic.name);
                          const base = derived.length >= 2 ? derived : randomCode();
                          setBasic((b) => ({ ...b, code: pickAvailableCode(base, existingTemplates, initial?.id) }));
                        }}
                        className="text-[10px] text-blue-600 hover:text-blue-700 underline"
                        data-testid="family-code-regen"
                      >
                        再生成
                      </button>
                    )}
                  </div>
                  <Input
                    data-testid="family-code-input"
                    value={basic.code}
                    onChange={(e) => {
                      setCodeManuallyEdited(true);
                      setBasic({ ...basic, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') });
                    }}
                    placeholder="例: door_parts"
                    disabled={editing}
                    aria-invalid={!editing && !!basic.code && !CODE_RE.test(basic.code)}
                    className={`font-mono ${!editing && !!basic.code && !CODE_RE.test(basic.code) ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`}
                  />
                  {(() => {
                    if (editing) {
                      return <div className="text-[10px] text-zinc-500">既存ルールのコードは変更できません</div>;
                    }
                    const codeInvalid = !!basic.code && !CODE_RE.test(basic.code);
                    const codeTooShort = !!basic.code && basic.code.length < 2;
                    if (codeInvalid) {
                      return (
                        <div className="text-[11px] text-rose-600" data-testid="family-code-error">
                          {codeTooShort
                            ? `あと ${2 - basic.code.length} 文字以上入力してください（半角英数字と _ のみ）`
                            : '半角英数字と _ のみ、2〜32文字で入力してください'}
                        </div>
                      );
                    }
                    return (
                      <div className="text-[10px] text-zinc-500">
                        監査ログや外部連携で使う内部識別子です。半角英数字と _ のみ / 2〜32文字。
                      </div>
                    );
                  })()}
                  {/* 対象アプリ (opt-in — most users don't need this) */}
                  <div className="pt-2 mt-2 border-t border-zinc-200">
                    <Label className="text-xs font-semibold text-zinc-700">対象アプリ（任意）</Label>
                    <Select value={appCode || '__any__'} onValueChange={(v) => setAppCode(v === '__any__' ? '' : v)}>
                      <SelectTrigger className="mt-0.5" data-testid="family-app-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {APP_CHOICES.map((a) => (
                          <SelectItem
                            key={a.code || '__any__'}
                            value={a.code || '__any__'}
                            data-testid={`family-app-opt-${a.code || 'any'}`}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm">{a.label}</span>
                              <span className="text-[10px] text-zinc-500">{a.hint}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      通常は「すべてのアプリで使用可」で問題ありません
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1 — 大分類 */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="text-sm font-semibold text-zinc-800">大分類 の作り方を選んでください</div>
              <SegmentPicker value={cat1 || {}} onChange={setCat1} positionLabel="大分類" showNextSep />
              <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
                <Label className="text-xs flex items-center gap-1 flex-wrap">
                  この大分類の種別（資産 / 消耗品）
                  <span className="text-[10px] text-zinc-400 font-normal">· 全アプリ共通の属性です。発番時に各フォーム（IT資産など）へ種別が自動連携されます</span>
                </Label>
                <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid="family-asset-kind">
                  {[['', '未指定'], ['durable', '資産（減価償却）'], ['consumable', '消耗品（数量管理）']].map(([v, label]) => (
                    <button key={v || 'none'} type="button" onClick={() => setAssetKind(v)}
                      data-testid={`family-asset-kind-${v || 'none'}`}
                      className={`h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${
                        assetKind === v
                          ? (v === 'consumable' ? 'bg-purple-600 text-white border-purple-600' : v === 'durable' ? 'bg-sky-600 text-white border-sky-600' : 'bg-zinc-700 text-white border-zinc-700')
                          : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — 中分類 */}
          {step === 2 && (
            <div className="space-y-3">
              {!use2 ? (
                <div className="rounded-xl border-2 border-dashed border-zinc-200 p-6 text-center space-y-3">
                  <div className="text-sm text-zinc-600">
                    中分類（Category 2）を追加しますか？
                    <div className="text-xs text-zinc-500 mt-1">
                      例: 大分類「SMH」＋ 中分類「連番5桁」→ 「SMH00001」
                    </div>
                  </div>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={() => { setUse2(false); setStep(3); }} data-testid="skip-cat2-btn">
                      スキップ
                    </Button>
                    <Button onClick={() => setUse2(true)} data-testid="add-cat2-btn">
                      <Sparkle size={14} className="mr-1" /> 中分類を追加
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-zinc-800">中分類 の作り方を選んでください</div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setUse2(false); setCat2(null); }}
                      data-testid="remove-cat2-btn"
                    >
                      <Trash size={14} className="mr-1" /> 中分類を消す
                    </Button>
                  </div>
                  <SegmentPicker value={cat2 || {}} onChange={setCat2} positionLabel="中分類" showNextSep />
                </>
              )}
            </div>
          )}

          {/* Step 3 — 小分類 */}
          {step === 3 && (
            <div className="space-y-3">
              {!use2 ? (
                <div className="rounded-xl border-2 border-dashed border-zinc-200 p-6 text-center text-sm text-zinc-500">
                  中分類がまだ無いため、小分類は追加できません。先に中分類を追加してください。
                </div>
              ) : !use3 ? (
                <div className="rounded-xl border-2 border-dashed border-zinc-200 p-6 text-center space-y-3">
                  <div className="text-sm text-zinc-600">
                    小分類（Category 3）を追加しますか？
                    <div className="text-xs text-zinc-500 mt-1">
                      3階層まで作れます。ほとんどの場合は 2階層で十分です。
                    </div>
                  </div>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={() => { setUse3(false); setStep(4); }} data-testid="skip-cat3-btn">
                      スキップ
                    </Button>
                    <Button onClick={() => setUse3(true)} data-testid="add-cat3-btn">
                      <Sparkle size={14} className="mr-1" /> 小分類を追加
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-zinc-800">小分類 の作り方を選んでください</div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setUse3(false); setCat3(null); }}
                      data-testid="remove-cat3-btn"
                    >
                      <Trash size={14} className="mr-1" /> 小分類を消す
                    </Button>
                  </div>
                  <SegmentPicker value={cat3 || {}} onChange={setCat3} positionLabel="小分類" showNextSep={false} />
                </>
              )}
            </div>
          )}

          {/* Step 4 — Preview */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="rounded-xl border-2 border-blue-200 bg-zinc-50 p-5 text-center">
                <div className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">サンプル品番</div>
                <div className="text-3xl font-mono font-bold text-blue-900 mt-1 tracking-wider" data-testid="family-preview-sku">
                  {previewSku || '-'}
                </div>
                <div className="text-xs text-zinc-500 mt-1">全体の長さ: {totalWidth}文字</div>
              </div>

              <div className="rounded-lg border p-3 space-y-2 text-sm">
                <div className="font-semibold text-zinc-800">品番の組み立て</div>
                {segments.length === 0 && <div className="text-xs text-zinc-500">区分がまだありません</div>}
                {(() => {
                  // iter_144a · Position labels only apply to the "real" categories.
                  // Auto-injected separators get a distinct chip so users see them
                  // as glue between the two flanking categories.
                  const posLabels = ['大分類', '中分類', '小分類'];
                  let catIdx = 0;
                  return segments.map((s, i) => {
                    const t = SEG_TYPE_BY_KEY[s._wiz.type];
                    const Icon = t?.icon || Hash;
                    const isAutoSep = !!s._autoSep;
                    const posLabel = isAutoSep ? '区切り' : (posLabels[catIdx++] || `区分${i + 1}`);
                    return (
                      <div key={i} className={`flex items-center gap-2 py-1.5 border-b last:border-b-0 ${isAutoSep ? 'bg-slate-50/60 rounded' : ''}`}>
                        <span className={`text-[10px] font-semibold w-14 ${isAutoSep ? 'text-slate-500' : 'text-zinc-500'}`}>{posLabel}</span>
                        <span className={`w-2 h-2 rounded-full ${t?.dot || 'bg-zinc-400'}`} />
                        <Icon size={16} className={isAutoSep ? 'text-slate-500' : 'text-zinc-600'} />
                        <span className={`text-xs flex-1 ${isAutoSep ? 'text-slate-500' : 'text-zinc-600'}`}>
                          {isAutoSep ? 'セパレーター (次との間)' : t?.label}
                        </span>
                        <span className="font-mono text-sm bg-zinc-100 px-2 py-0.5 rounded">{previewSegment(s._wiz)}</span>
                        <span className="text-[10px] text-zinc-400">{s.width}文字</span>
                      </div>
                    );
                  });
                })()}
              </div>

              {!hasDynamic && segments.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2">
                  <Warning size={14} className="shrink-0 mt-0.5" />
                  この設定だと全ての品番が同じ文字列になります。「連番」「年」「月」のどれかを追加することをお勧めします。
                </div>
              )}

              <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-2" data-testid="family-group-block">
                <Label className="text-xs flex items-center gap-1 flex-wrap">
                  品番グループ <span className="text-rose-500">*</span>
                  <span className="text-[10px] text-zinc-400 font-normal">
                    · このルールで出した番号は、どの登録画面でもこのグループとして扱われます
                  </span>
                </Label>
                {!newGroup ? (
                  <div className="flex gap-2 flex-wrap items-center">
                    <select
                      value={groupCode}
                      onChange={(e) => setGroupCode(e.target.value)}
                      className="h-9 min-w-[16rem] rounded-md border border-zinc-200 bg-white px-2 text-sm"
                      data-testid="family-sku-group-select"
                    >
                      <option value="">選択してください</option>
                      {skuGroups.map((g) => (
                        <option key={g.code} value={g.code}>{g.code} — {g.name_ja}</option>
                      ))}
                    </select>
                    <Button
                      type="button" variant="outline" size="sm" className="h-9 text-[11px]"
                      onClick={() => setNewGroup({ code: rulePrefix, name_ja: '', default_category_id: 'component' })}
                      data-testid="family-sku-group-new"
                    >
                      <Plus size={12} className="mr-1" /> 新しいグループを作成
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="family-sku-group-new-form">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] text-zinc-500">コード *</Label>
                        <Input
                          value={newGroup.code}
                          disabled={!!rulePrefix}
                          onChange={(e) => setNewGroup((g) => ({ ...g, code: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                          className="font-mono disabled:bg-zinc-100"
                          data-testid="family-sku-group-code"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-zinc-500">名称 *</Label>
                        <Input
                          value={newGroup.name_ja}
                          onChange={(e) => setNewGroup((g) => ({ ...g, name_ja: e.target.value }))}
                          placeholder="例: 空圧部品"
                          data-testid="family-sku-group-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">デフォルトカテゴリ</Label>
                      <select
                        value={newGroup.default_category_id}
                        onChange={(e) => setNewGroup((g) => ({ ...g, default_category_id: e.target.value }))}
                        className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                        data-testid="family-sku-group-cat"
                      >
                        {assetCats.filter((c) => c.selectable !== false)
                          .map((c) => <option key={c.id} value={c.id}>{c.name_ja}</option>)}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewGroup(null)}
                      className="text-[11px] text-blue-600 underline"
                      data-testid="family-sku-group-cancel-new"
                    >
                      既存のグループから選ぶ
                    </button>
                  </div>
                )}
              </div>

              {errors.length > 0 && (
                <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800 space-y-1">
                  {errors.map((e) => (
                    <div key={e} className="flex items-center gap-1.5">
                      <Warning size={12} /> {e}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step > 0 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="wizard-back-btn">
                <ArrowLeft size={14} className="mr-1" /> 戻る
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              キャンセル
            </Button>
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={
                  // iter_144c · Each category step now also requires a non-empty
                  // label per user "please make the naming of 分類 mandatory".
                  (step === 0 && (!basic.name || !basic.code || (!editing && !CODE_RE.test(basic.code)))) ||
                  (step === 1 && (!cat1?.type || !(cat1.label || '').trim() || ((cat1.type === 'letters' || cat1.type === 'digits') && !cat1.value))) ||
                  (step === 2 && use2 && (!cat2?.type || !(cat2.label || '').trim() || ((cat2.type === 'letters' || cat2.type === 'digits') && !cat2.value))) ||
                  (step === 3 && use3 && (!cat3?.type || !(cat3.label || '').trim() || ((cat3.type === 'letters' || cat3.type === 'digits') && !cat3.value)))
                }
                data-testid="wizard-next-btn"
              >
                次へ <ArrowRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button
                onClick={save}
                disabled={!canSave || busy}
                data-testid="wizard-save-btn"
              >
                {busy ? '保存中…' : editing ? <><PencilSimple size={14} className="mr-1" /> 上書き保存</> : <><Check size={14} className="mr-1" /> 作成する</>}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
